import * as React from 'react';
import { useRouter } from 'next/router';
import { useTranslate } from '@tolgee/react';

import { CheersHubIcon, OtherAppsIcon, OurAppsIcon } from '~/shared/components/icons';

import { useMenuRoutes } from '../components/Nav/hooks/useMenuRoutes';
import { RouteObject } from '../types';

import { useAbility } from './AbilityContext';

type NavContextData = {
    isAppMenuOpen: boolean;
    subMenuOpen?: RouteObject | null;
    menuHeaderItem: RouteObject;
    menuItems: RouteObject[];
    footerItems: RouteObject[];
    toggleAppMenu: () => void;
    toggleSubMenu: (item: RouteObject | null) => void;
    checkIfIsActive: (menuRoute: string, subitems?: RouteObject[]) => boolean;
};

const NavContext = React.createContext<NavContextData | undefined>(undefined);

type NavProviderProps = {
    children: React.ReactNode;
};

export function NavProvider({ children }: NavProviderProps) {
    const { t } = useTranslate();
    const ability = useAbility();

    const router = useRouter();

    const [isAppMenuOpen, setIsAppMenuOpen] = React.useState(false);
    const [subMenuOpen, setSubmenuOpen] = React.useState<RouteObject | null>();
    const { menuRoutes, footerRoutes } = useMenuRoutes();

    const toggleSubMenu = (item: RouteObject | null) => {
        setSubmenuOpen(item?.routeComponents?.length ? item : null);
    };

    const checkIfIsActive = React.useCallback(
        (menuRoute: string) => !!menuRoute && router.route === menuRoute,
        [router.route],
    );

    const ourApps: RouteObject[] = React.useMemo(() => {
        const values: RouteObject[] = [
            {
                key: 'compensation',
                title: 'Compensation',
                path: 'https://rewards.ab-inbev.com/',
                icon: <OtherAppsIcon />,
            },
            {
                key: 'northstar',
                title: 'NorthStar',
                path: 'https://northstar.ab-inbev.com/',
                icon: <OtherAppsIcon />,
            },
            {
                key: 'opr',
                title: 'OPR',
                path: 'https://opr.ab-inbev.com/home',
                icon: <OtherAppsIcon />,
            },
            {
                key: 'workday',
                title: 'Workday',
                path: 'https://www.myworkday.com/abinbev/d/home.htmld',
                icon: <OtherAppsIcon />,
            },
        ];

        if (ability.can('view', 'Cheershub')) {
            values.unshift({
                key: 'cheersHub',
                title: 'CheersHub',
                path: 'https://rewards.ab-inbev.com',
                icon: <CheersHubIcon />,
            });
        }

        return values;
    }, [ability]);

    function toggleAppMenu() {
        setIsAppMenuOpen((state) => !state);
    }

    const providerValue = React.useMemo(
        () => ({
            isAppMenuOpen,
            subMenuOpen,
            menuItems: menuRoutes,
            footerItems: footerRoutes,
            menuHeaderItem: {
                key: 'our_apps',
                title: t('common.menu_our_apps'),
                path: '/',
                icon: <OurAppsIcon />,
                routeComponents: ourApps,
            },
            toggleAppMenu,
            toggleSubMenu,
            checkIfIsActive,
        }),
        [isAppMenuOpen, subMenuOpen, menuRoutes, footerRoutes, t, ourApps, checkIfIsActive],
    );

    return <NavContext.Provider value={providerValue}>{children}</NavContext.Provider>;
}

export function useNav() {
    const context = React.useContext(NavContext);

    if (!context) {
        throw new Error('useNav must be used within a NavProvider');
    }

    return context;
}
